import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiSearch, FiTrendingUp, FiStar, FiDownload, FiShield, FiZap, FiGlobe, FiLock, FiMessageCircle, FiPlay, FiCode, FiShoppingCart } from 'react-icons/fi';
import axios from 'axios';

const categories = [
  { id: 'all', name: 'All', icon: FiGlobe },
  { id: 'productivity', name: 'Productivity', icon: FiZap },
  { id: 'privacy', name: 'Privacy & Security', icon: FiLock },
  { id: 'social', name: 'Social', icon: FiMessageCircle },
  { id: 'entertainment', name: 'Entertainment', icon: FiPlay },
  { id: 'developer-tools', name: 'Developer Tools', icon: FiCode },
  { id: 'shopping', name: 'Shopping', icon: FiShoppingCart },
];

function Home() {
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchExtensions();
  }, [selectedCategory, searchQuery]);

  const fetchExtensions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://extension.avalw.com/api/extensions', {
        params: {
          category: selectedCategory !== 'all' ? selectedCategory : undefined,
          search: searchQuery || undefined
        }
      });
      setExtensions(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error fetching extensions:', error);
      setExtensions([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white py-24 border-b border-gray-200">
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-6xl font-bold mb-6 text-gray-900">
              AVALW Extension Marketplace
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Discover powerful extensions to supercharge your Cronos Browser experience
            </p>

            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <FiSearch className="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
              <input
                type="text"
                placeholder="Search extensions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-14 pr-6 py-4 bg-white border-2 border-gray-200 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-900 transition-all shadow-sm"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-6">
          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
            {categories.map((cat) => {
              const IconComponent = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full whitespace-nowrap transition-all ${
                    selectedCategory === cat.id
                      ? 'bg-gray-900 text-white shadow-lg'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <IconComponent className="text-lg" />
                  <span className="font-medium">{cat.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Extensions Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">
              {selectedCategory === 'all' ? 'All Extensions' : categories.find(c => c.id === selectedCategory)?.name}
            </h2>
            <select className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:border-gray-900">
              <option value="popular">Most Popular</option>
              <option value="recent">Recently Added</option>
              <option value="rating">Highest Rated</option>
            </select>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-gray-50 rounded-2xl p-6 animate-pulse border border-gray-200">
                  <div className="w-16 h-16 bg-gray-200 rounded-xl mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {extensions.map((ext) => (
                <ExtensionCard key={ext.id} extension={ext} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50 border-t border-gray-200">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">Why Choose AVALW Extensions?</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<FiShield className="text-5xl text-gray-900" />}
              title="Secure & Verified"
              description="All extensions are thoroughly reviewed and scanned for security"
            />
            <FeatureCard
              icon={<FiZap className="text-5xl text-gray-900" />}
              title="Powerful API"
              description="Build extensions with our comprehensive JavaScript API"
            />
            <FeatureCard
              icon={<FiStar className="text-5xl text-gray-900" />}
              title="Easy to Develop"
              description="Simple .avalw format with extensive documentation and tutorials"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function ExtensionCard({ extension }) {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-gray-900 transition-all cursor-pointer group shadow-sm hover:shadow-md"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center text-3xl border border-gray-200">
          {extension.icon || '🧩'}
        </div>
        <div className="flex items-center gap-1 text-gray-900">
          <FiStar className="fill-current" />
          <span className="font-semibold">{extension.rating || '4.5'}</span>
        </div>
      </div>

      <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-gray-700 transition-colors">
        {extension.name}
      </h3>
      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
        {extension.description}
      </p>

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1 text-gray-500">
          <FiDownload />
          <span>{extension.downloadCount || 0}</span>
        </div>
        <button className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all font-medium">
          Install
        </button>
      </div>
    </motion.div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      className="text-center p-8 bg-white rounded-2xl border-2 border-gray-200 shadow-sm hover:shadow-md transition-all"
    >
      <div className="flex justify-center mb-6">{icon}</div>
      <h3 className="text-2xl font-bold mb-4 text-gray-900">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </motion.div>
  );
}

export default Home;
